import { AxiosProgressEvent } from 'axios';
import {
  ChatRequest,
  CreateEarlyPhaseAssessmentPayload,
  CreateEarlyPhaseAssessmentRequest,
  CreateProjectEarlyPhaseAssessmentRequest,
  GetDocumentsListRequest,
  GetEarlyPhaseAssessmentDependenciesRequest,
  GetEvaluationInsightsRequest,
  SetEpaAnnotationsRequest,
  UpdateDependencyRiskRequest,
} from '../types/Dtos/request';
import {
  CreateEarlyPhaseAssessmentResponse,
  GetEarlyPhaseAssessmentDependenciesResponse,
  GetEarlyPhaseAssessmentDependenciesStatusResponse,
  GetEarlyPhaseAssessmentDetailsResponse,
  getEPAAnnotationDocumentsResponse,
} from '../types/Dtos/response';
import { api, getQueryParamsFromObj } from '../utils/api';
import { EarlyPhaseDependencyDetails, EpaAnnotation } from '../types/models';
import { DocumentTypeEnum, ProxyEvaluationTypeEnum } from '../utils/constants';
import {
  deleteDocuments,
  getIndexingStatus,
  listDocuments,
  runAnalysis,
  uploadSingleDocument,
} from './documentsService';
import { connectToChatStream } from './chatService';
import {
  createEvaluation,
  deleteEvaluation,
  getEvaluationDetails,
  getEvaluationInsights,
  starEvaluationStatement,
  unstarEvaluationStatement,
  updateEvaluation,
} from './evaluationsService';

const RESOURCE_TYPE = DocumentTypeEnum.EARLY_PHASE_ASSESSMENT; // 80

/** Evaluation - START */
export const createProjectEPA = ({
  organizationId,
  projectId,
  payload,
}: CreateProjectEarlyPhaseAssessmentRequest) =>
  createEvaluation(organizationId, {
    ...payload,
    projectId,
    resourceType: RESOURCE_TYPE,
  }) as Promise<import('axios').AxiosResponse<CreateEarlyPhaseAssessmentResponse>>;

export const createEPA = ({ organizationId, payload }: CreateEarlyPhaseAssessmentRequest) =>
  createEvaluation(organizationId, {
    ...payload,
    resourceType: RESOURCE_TYPE,
  }) as Promise<import('axios').AxiosResponse<CreateEarlyPhaseAssessmentResponse>>;

export const deleteEPA = (earlyPhaseAssessmentId: string) =>
  deleteEvaluation(earlyPhaseAssessmentId);

export const updateEPA = (
  earlyPhaseAssessmentId: string,
  payload: CreateEarlyPhaseAssessmentPayload,
) => updateEvaluation(earlyPhaseAssessmentId, { ...payload });

export const getEPADetails = (earlyPhaseAssessmentId: string) =>
  getEvaluationDetails(earlyPhaseAssessmentId) as Promise<
    import('axios').AxiosResponse<GetEarlyPhaseAssessmentDetailsResponse>
  >;

export const getEPAInsights = ({ evaluationId, ...payload }: GetEvaluationInsightsRequest) =>
  getEvaluationInsights({ evaluationId, ...payload });

/** Evaluation - END */

/** Documents - START */
export const getEPADocuments = ({
  id,
  orderBy,
  orderDirection,
  searchText,
}: GetDocumentsListRequest) =>
  listDocuments(
    ProxyEvaluationTypeEnum.earlyPhaseAssessment,
    id,
    orderBy,
    orderDirection,
    searchText,
  );

export const analyseEPADocuments = (earlyPhaseAssessmentId: string) =>
  runAnalysis(ProxyEvaluationTypeEnum.earlyPhaseAssessment, earlyPhaseAssessmentId);

export const uploadEarlyPhaseAssessmentDocument = (
  file: File,
  earlyPhaseAssessmentId: string,
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
) =>
  uploadSingleDocument(
    file,
    ProxyEvaluationTypeEnum.earlyPhaseAssessment,
    earlyPhaseAssessmentId,
    undefined,
    onUploadProgress,
  );

export const deleteEPADocuments = (earlyPhaseAssessmentId: string, documentIds: string[]) =>
  deleteDocuments(
    ProxyEvaluationTypeEnum.earlyPhaseAssessment,
    earlyPhaseAssessmentId,
    documentIds,
  );
/** Documents - END */

/** Chat - START */
export const connectToEPAChatStream = ({
  id,
  conversationId,
  userPrompt,
  specifiedDocuments,
  clarificationResponse,
}: ChatRequest) => {
  const body = {
    chat_session_id: `${conversationId}`,
    eval_id: id,
    eval_type: ProxyEvaluationTypeEnum.earlyPhaseAssessment,
    query: userPrompt,
    specified_documents: specifiedDocuments,
    clarification_response: undefined,
  };
  if (clarificationResponse) body.clarification_response = clarificationResponse;
  return connectToChatStream(id, ProxyEvaluationTypeEnum.earlyPhaseAssessment, body);
};
/** Chat - END */

/** Summarization - START */
export const markEPAStatementAsStarred = (earlyPhaseAssessmentId: string, statementId: string) =>
  starEvaluationStatement(earlyPhaseAssessmentId, statementId);

export const unmarkEPAStatementAsStarred = (earlyPhaseAssessmentId: string, statementId: string) =>
  unstarEvaluationStatement(earlyPhaseAssessmentId, statementId);

export const exportEPAReport = (earlyPhaseAssessmentId: string) =>
  api.get<Blob>(`earlyphaseassessments/${earlyPhaseAssessmentId}/overview/export`, {
    responseType: 'blob',
  });
/** Summarization - END */

/** Dependencies - START (stays on .NET) */
export const triggerEPADependenciesAnalysis = (earlyPhaseAssessmentId: string) =>
  api.put(`earlyphaseassessments/${earlyPhaseAssessmentId}/dependencies/analysis`);

export const getEPADependenciesStatus = (earlyPhaseAssessmentId: string) =>
  api.get<GetEarlyPhaseAssessmentDependenciesStatusResponse>(
    `earlyphaseassessments/${earlyPhaseAssessmentId}/dependencies/status`,
  );

export const getEPADependencies = ({
  earlyPhaseAssessmentId,
  ...payload
}: GetEarlyPhaseAssessmentDependenciesRequest) =>
  api.get<GetEarlyPhaseAssessmentDependenciesResponse>(
    `earlyphaseassessments/${earlyPhaseAssessmentId}/dependencies?${getQueryParamsFromObj(payload)}`,
  );

export const getEPADependency = (earlyPhaseAssessmentId: string, dependencyId: string) =>
  api.get<EarlyPhaseDependencyDetails>(
    `earlyphaseassessments/${earlyPhaseAssessmentId}/dependencies/${dependencyId}`,
  );

export const getEPAAnnotationDocuments = (earlyPhaseAssessmentId: string) =>
  api.get<getEPAAnnotationDocumentsResponse>(
    `earlyphaseassessments/${earlyPhaseAssessmentId}/dependencies/documentsforannotations`,
  );

export const getEPADocAnnotations = (earlyPhaseAssessmentId: string, documentId: string) =>
  api.get<EpaAnnotation[]>(
    `earlyphaseassessments/${earlyPhaseAssessmentId}/dependencies/annotations/document/${documentId}`,
  );

export const setEpaAnnotations = (
  earlyPhaseAssessmentId: string,
  payload: SetEpaAnnotationsRequest,
) => api.put(`earlyphaseassessments/${earlyPhaseAssessmentId}/dependencies/annotations`, payload);

export const getEpaAnnotationsTotalCount = (earlyPhaseAssessmentId: string) =>
  api.get<number>(`earlyphaseassessments/${earlyPhaseAssessmentId}/dependencies/annotations/count`);

export const updateDependencyRisk = ({
  earlyPhaseAssessmentId,
  dependencyId,
  risk,
}: UpdateDependencyRiskRequest) =>
  api.put(`earlyphaseassessments/${earlyPhaseAssessmentId}/dependencies/${dependencyId}/risk`, {
    risk,
  });
/** Dependencies - END */

export const getProxyEPAIndexedDocumentsStatus = (earlyPhaseAssessmentId: string) =>
  getIndexingStatus(ProxyEvaluationTypeEnum.earlyPhaseAssessment, earlyPhaseAssessmentId);
